import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";

import { TempLogo } from "@/assets/temp-logo";
import Footer from "@/components/Common/Footer";
import { useAuth } from "../hooks/useAuth";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import { useLoginWithHashKey } from "../api/hash.queries";
import { extractErrorMessage } from "@/lib/errors";
import { Spinner } from "@/components/ui/spinner";

const loginWithHashKeySchema = z.object({
  hash_key: z.string().min(1, "Hash key is required"),
});

export type LoginWithHashKeySchema = z.infer<typeof loginWithHashKeySchema>;

export default function Hash() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Redirect if logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: "/", search: { section: undefined } });
    }
  }, [isAuthenticated, navigate]);

  const form = useForm<LoginWithHashKeySchema>({
    resolver: zodResolver(loginWithHashKeySchema),
    defaultValues: {
      hash_key: "",
    },
  });

  const { mutate: loginWithHashKey, isPending } = useLoginWithHashKey();
  const { setError } = form;

  function onSubmit(values: LoginWithHashKeySchema) {
    loginWithHashKey(values, {
      // Remove onSuccess navigation since it's handled in the mutation
      onError: (err: any) => {
        setError("hash_key", {
          message: extractErrorMessage(err),
        });
      },
    });
  }
  return (
    <div className="flex min-h-screen flex-col items-center justify-between px-4 pb-8">
      <div className="flex w-full max-w-[420px] flex-grow flex-col justify-center gap-[60px]">
        {/* Logo */}
        <div className="text-center">
          <div className="flex justify-center">
            <TempLogo width={68} height={44} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">metawhale</h1>
        </div>

        <div className="space-y-10">
          {/* Back */}
          <p
            className="text-muted flex cursor-pointer items-center gap-1 text-sm font-bold"
            onClick={() => navigate({ to: "/login" })}
          >
            <i className="ri-arrow-left-long-line"></i>
            Back
          </p>

          {/* Form */}
          <div className="space-y-10">
            <div className="space-y-1">
              <Label className="text-sm font-bold text-neutral-800">
                Hash Key <span className="text-red-600">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter your hash key"
                variant="filled"
                className="h-[45px]"
                {...form.register("hash_key")}
              />
            </div>
            <Button
              type="submit"
              className="text-md h-[48px] w-full leading-normal"
              disabled={isPending}
              onClick={form.handleSubmit(onSubmit)}
            >
              {isPending ? (
                <div className="flex h-full items-center justify-center text-white">
                  <Spinner size="sm" className="stroke-white!" />
                </div>
              ) : (
                "Login"
              )}
            </Button>
          </div>
        </div>
        <div className="text-center text-sm font-semibold text-neutral-800">
          If you have any questions, please contact <br />
          <a
            href="mailto:support@media-meter.com"
            className="text-brand-600 hover:text-orange-600"
          >
            support@media-meter.com
          </a>
          . We are happy to help!
        </div>
      </div>
      <Footer />
    </div>
  );
}
